import React, { useRef, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Title,
  BarElement,
} from "chart.js";

// Register the required Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Title,
  BarElement
);

const LineChartComponent = ({ data, options }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const dataRef = useRef(data);
  const optionsRef = useRef(options);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // First render of the chart
  useEffect(() => {
    // Don't recreate chart instance on every render
    if (!chartRef.current) return;
    
    // Only create the chart once
    if (!chartInstance.current) {
      const ctx = chartRef.current.getContext('2d');
      
      // Create safe data with string colors
      const safeData = {
        ...data,
        datasets: data.datasets.map(dataset => ({
          ...dataset,
          borderColor: typeof dataset.borderColor === 'string' ? dataset.borderColor : '#999999',
          backgroundColor: typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : 'rgba(153, 153, 153, 0.1)'
        }))
      };
      
      // Enhanced animation settings
      const enhancedOptions = {
        ...options,
        animation: {
          duration: 1000,
          easing: 'easeOutQuart',
          delay: (context) => {
            // Add staggered delay based on dataset index
            const datasetIndex = context.datasetIndex || 0;
            return datasetIndex * 100;
          }
        },
        transitions: {
          active: {
            animation: {
              duration: 300
            }
          }
        }
      };
      
      setIsAnimating(true);
      
      // Create chart with enhanced animations
      chartInstance.current = new ChartJS(ctx, {
        type: 'line',
        data: safeData,
        options: enhancedOptions
      });
      
      // Handle animation end
      chartInstance.current.options.animation.onComplete = () => {
        setIsAnimating(false);
      };
      
      // Store initial data and options
      dataRef.current = data;
      optionsRef.current = options;
    }
    
    // Cleanup on unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []); // Empty dependency array so this only runs once
  
  // Separate effect for updating the chart when data/options change
  useEffect(() => {
    if (!chartInstance.current) return;
    
    // Shallow comparison to see if data changed
    const dataChanged = JSON.stringify(dataRef.current) !== JSON.stringify(data);
    const optionsChanged = JSON.stringify(optionsRef.current) !== JSON.stringify(options);
    
    if (dataChanged || optionsChanged) {
      // Update refs
      dataRef.current = data;
      optionsRef.current = options;
      
      // Create safe data with string colors
      const safeData = {
        ...data,
        datasets: data.datasets.map(dataset => ({
          ...dataset,
          borderColor: typeof dataset.borderColor === 'string' ? dataset.borderColor : '#999999',
          backgroundColor: typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : 'rgba(153, 153, 153, 0.1)'
        }))
      };
      
      // Enhanced animation settings for updates
      const enhancedOptions = {
        ...options,
        animation: {
          duration: 800,
          easing: 'easeOutCubic'
        }
      };
      
      // Set animating state
      setIsAnimating(true);
      
      // Update chart data and options
      chartInstance.current.data = safeData;
      chartInstance.current.options = enhancedOptions;
      
      // Update with animations
      chartInstance.current.update();
      
      // Handle animation end
      chartInstance.current.options.animation.onComplete = () => {
        setIsAnimating(false);
      };
    }
  }, [data, options]);
  
  return (
    <div className={`relative w-full h-full ${isAnimating ? 'animate-pulse-subtle' : ''}`}>
      <canvas ref={chartRef} />
    </div>
  );
};

export default LineChartComponent; 